import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Product, ProductCategory } from '@/types/product.types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Package } from 'lucide-react';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';

interface ProductListProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (productId: string) => void;
  onAdd: () => void;
}

const CATEGORY_COLORS: Record<ProductCategory, string> = {
  'Légumes': 'bg-green-100 text-green-800',
  'Viandes': 'bg-red-100 text-red-800',
  'Poissons': 'bg-blue-100 text-blue-800',
  'Laitages': 'bg-yellow-100 text-yellow-800',
  'Fruits': 'bg-orange-100 text-orange-800',
  'Épices': 'bg-pink-100 text-pink-800',
  'Condiments': 'bg-lime-100 text-lime-800',
  // Réserve
  'Conserves': 'bg-amber-100 text-amber-800',
  'Produits secs': 'bg-stone-100 text-stone-800',
  'Huiles et vinaigres': 'bg-yellow-100 text-yellow-800',
  'Pâtes et riz': 'bg-amber-100 text-amber-800',
  'Farines et céréales': 'bg-orange-100 text-orange-800',
  // Boissons
  'Boissons non alcoolisées': 'bg-cyan-100 text-cyan-800',
  'Alcools': 'bg-purple-100 text-purple-800',
  'Vins': 'bg-red-100 text-red-800',
  'Bières': 'bg-yellow-100 text-yellow-800',
  // Entretien
  'Produits d\'entretien': 'bg-indigo-100 text-indigo-800',
  'Produits d\'hygiène': 'bg-sky-100 text-sky-800',
  'Emballages': 'bg-slate-100 text-slate-800',
  'Matériel jetable': 'bg-gray-100 dark:bg-gray-700 text-gray-800',
  // Autres
  'Autres': 'bg-gray-100 dark:bg-gray-700 text-gray-800',
};

export function ProductList({ products, onEdit, onDelete, onAdd }: ProductListProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | 'all'>('all');
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  // Filtrer les produits
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.code_barre?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || product.categorie === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Obtenir les catégories uniques
  const categories: ProductCategory[] = [
    'Légumes',
    'Viandes',
    'Poissons',
    'Laitages',
    'Fruits',
    'Épices',
    'Condiments',
    'Conserves',
    'Produits secs',
    'Huiles et vinaigres',
    'Pâtes et riz',
    'Farines et céréales',
    'Boissons non alcoolisées',
    'Alcools',
    'Vins',
    'Bières',
    'Produits d\'entretien',
    'Produits d\'hygiène',
    'Emballages',
    'Matériel jetable',
    'Autres',
  ];

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* En-tête avec recherche et bouton d'ajout */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        <Input
          type="text"
          placeholder="Rechercher un produit..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Button onClick={onAdd} className="w-full sm:w-auto whitespace-nowrap">
          <span className="hidden sm:inline">Ajouter un produit</span>
          <span className="sm:hidden">+ Ajouter</span>
        </Button>
      </div>

      {/* Filtres par catégorie */}
      <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap -mx-1 px-1">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
            selectedCategory === 'all'
              ? 'bg-primary text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          Toutes
        </button>
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
              selectedCategory === category
                ? CATEGORY_COLORS[category]
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Liste des produits */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-8 sm:py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Aucun produit trouvé</p>
          <Button onClick={onAdd} variant="outline" className="mt-4" size="sm">
            Créer votre premier produit
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 sm:p-4 hover:shadow-md transition-shadow border border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">{product.nom}</h3>
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                      CATEGORY_COLORS[product.categorie]
                    }`}
                  >
                    {product.categorie}
                  </span>
                </div>
              </div>

              <div className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                <p>
                  <span className="font-medium">Unité:</span> {product.unite}
                </p>
                {product.cout_unitaire && (
                  <p>
                    <span className="font-medium">Prix:</span> {product.cout_unitaire.toFixed(2)}€ / {product.unite}
                  </p>
                )}
                {product.code_barre && (
                  <p className="truncate">
                    <span className="font-medium">Code-barre:</span> {product.code_barre}
                  </p>
                )}
                <div className="flex gap-2 sm:gap-4">
                  <p>
                    <span className="font-medium">Alerte:</span> {product.seuil_alerte}
                  </p>
                  <p>
                    <span className="font-medium">Critique:</span> {product.seuil_critique}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 flex-col">
                <div className="flex gap-2">
                  <Button onClick={() => onEdit(product)} variant="outline" size="sm" className="flex-1 text-xs sm:text-sm">
                    Modifier
                  </Button>
                  <Button
                    onClick={async () => {
                      const confirmed = await confirm({
                        title: 'Supprimer le produit ?',
                        message: `Êtes-vous sûr de vouloir supprimer "${product.nom}" ?`,
                        confirmLabel: 'Supprimer',
                        variant: 'danger',
                      });
                      if (confirmed) {
                        onDelete(product.id);
                      }
                    }}
                    variant="danger"
                    size="sm"
                    className="flex-1 text-xs sm:text-sm"
                  >
                    Supprimer
                  </Button>
                </div>
                <Button
                  onClick={() => navigate('/inventory')}
                  variant="secondary"
                  size="sm"
                  className="w-full text-xs sm:text-sm"
                >
                  <Package size={14} className="mr-1" />
                  Gérer le stock
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {ConfirmDialogComponent}
    </div>
  );
}
